export type RecoveryChannel = {
  id: string
  name: string
  iconUrl?: string
}

const manymoonsMark = 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg'

// Discreet selling: the crossed-out eye mark. Rendered through a CSS mask so it
// picks up the off-white treatment the other channel logos use.
export const shushMark = 'https://cdn.magicpatterns.com/uploads/8bPGanqp7M59EsM6Sqmjgs/discreet-selling-icon.svg'

// Live selling wordmark, masked for the same reason.
export const liveMark = 'https://cdn.magicpatterns.com/uploads/dq2LGdECRWk5wKKAzVqRKi/M_live.svg'

// Marks that are solid black artwork and must be masked rather than shown as-is.
export const maskedMarks = [shushMark, liveMark]

export const recoveryChannels: RecoveryChannel[] = [
  { id: 'manymoons', name: 'Manymoons', iconUrl: manymoonsMark },
  { id: 'prive', name: 'Privé', iconUrl: 'https://cdn.magicpatterns.com/uploads/fHmVmW1X2zeRckQHVqfLBY/prive.svg' },
  { id: 'tiktok', name: 'Live Selling', iconUrl: liveMark },
  { id: 'whatnot', name: 'Discreet', iconUrl: shushMark },
  { id: 'ebay', name: 'eBay', iconUrl: 'https://cdn.magicpatterns.com/uploads/7ctVShw3HBQcunanqiLN75/ebay.svg' },
  { id: 'poshmark', name: 'Poshmark', iconUrl: 'https://cdn.magicpatterns.com/uploads/n5jkPZQbazKpxu5cMnVHR4/poshmark.svg' },
  { id: 'vinted', name: 'Vinted', iconUrl: 'https://cdn.magicpatterns.com/uploads/6n8bfmq2ixHXbjbeTaLCJz/vinted.svg' },
  { id: 'vestiaire', name: 'Vestiaire', iconUrl: 'https://cdn.magicpatterns.com/uploads/tLawC51vDdyw6q2RLyZ5Bc/vestiaire.svg' },
  { id: 'depop', name: 'Depop', iconUrl: 'https://cdn.magicpatterns.com/uploads/fG9XmMcsDQikNiC1uba9MJ/dopop.svg' },
  { id: 'mercari', name: 'Mercari', iconUrl: 'https://cdn.magicpatterns.com/uploads/cisCD3tYpjAhP5v7GoDZxD/mercari.svg' },
]

export const recoveryMarkets = ['North America', 'Latin America', 'Europe', 'Middle East', 'Africa', 'Asia', 'Oceania']

export type RecoveryRules = {
  channels: string[]
  markets: string[]
  // Countries switched off inside an otherwise enabled region, keyed by region id.
  excludedPlaces?: Record<string, string[]>
  indexPrice: number
  // The negotiated discount ceiling. While locked, the pricing limit is pinned here.
  indexLocked: boolean
  liveSellingAfterMonth: number
}

// The account-level defaults set in Settings. Every submission inherits these
// until someone overrides them for that specific upload.
export const defaultRecoveryRules: RecoveryRules = {
  channels: ['manymoons', 'ebay', 'whatnot'],
  markets: [...recoveryMarkets],
  indexPrice: 50,
  indexLocked: true,
  liveSellingAfterMonth: 3,
}

// Brands can carry their own negotiated starting point; everything else falls
// back to the account defaults above.
const brandOverrides: Record<string, Partial<RecoveryRules>> = {
  Nisolo: {
    indexPrice: 70,
    channels: ['manymoons', 'whatnot'],
    markets: ['North America', 'Europe', 'Asia', 'Oceania'],
  },
}

// Individual uploads can depart from their brand's footprint — damaged goods,
// for example, only move through live selling and discreet channels.
const submissionOverrides: Record<string, Partial<RecoveryRules>> = {
  '472 — Imperfects': { channels: ['manymoons', 'prive', 'tiktok', 'whatnot'] },
  '473 — Damages': { channels: ['tiktok', 'whatnot'] },
}

export function defaultRulesForBrand(brand?: string, batchName?: string): RecoveryRules {
  return {
    ...defaultRecoveryRules,
    ...(brand ? brandOverrides[brand] ?? {} : {}),
    ...(batchName ? submissionOverrides[batchName] ?? {} : {}),
  }
}

export function rulesMatchDefaults(rules: RecoveryRules, brand?: string, batchName?: string) {
  const base = defaultRulesForBrand(brand, batchName)
  return (
    rules.indexPrice === base.indexPrice &&
    rules.indexLocked === base.indexLocked &&
    rules.liveSellingAfterMonth === base.liveSellingAfterMonth &&
    rules.channels.length === base.channels.length &&
    rules.channels.every((id) => base.channels.includes(id)) &&
    rules.markets.length === base.markets.length &&
    rules.markets.every((market) => base.markets.includes(market))
  )
}

export function summarizeRules(rules: RecoveryRules) {
  return `${rules.channels.length} ${rules.channels.length === 1 ? 'channel' : 'channels'} · ${rules.markets.length} ${rules.markets.length === 1 ? 'market' : 'markets'} · ${rules.indexPrice}% off MSRP`
}
